import { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import {
  getUserNotifications,
  getUserNotificationsActiveCount,
  isApiConfigured,
} from "../apiClient";
import NotificationDropdown from "./NotificationDropdown";

function readAuthToken(authTokenProp) {
  const fromProp = String(authTokenProp || "").trim();
  if (fromProp) return fromProp;
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem("jobuwin-auth-token") || "").trim();
}

/**
 * Notifications bell + dropdown (live API). P1 applicant, P2 hiring per design guide.
 *
 * @param {{ currentUser?: { role?: string, id?: string } | null, authToken?: string }} props
 */
export default function HeaderNotificationBell({ currentUser, authToken: authTokenProp }) {
  const role = currentUser?.role;
  const accent = role === "hiring-manager" || role === "recruiter" ? "p2" : "p1";
  const [open, setOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refreshCount = useCallback(async () => {
    const token = readAuthToken(authTokenProp);
    if (!token || !currentUser || !isApiConfigured()) {
      setActiveCount(0);
      return;
    }
    try {
      const { count } = await getUserNotificationsActiveCount(token, role);
      setActiveCount(Number(count) || 0);
    } catch {
      setActiveCount(0);
    }
  }, [authTokenProp, currentUser, role]);

  const loadDropdown = useCallback(async () => {
    const token = readAuthToken(authTokenProp);
    if (!token || !currentUser || !isApiConfigured()) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getUserNotifications(token, role, { status: "active", limit: 10, offset: 0 });
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [authTokenProp, currentUser, role]);

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return undefined;
    void loadDropdown();
    const onDocPointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, loadDropdown]);

  if (!currentUser) {
    return null;
  }

  const showBadge = activeCount > 0;

  return (
    <div className="jw-notification-bell-host" ref={wrapRef}>
      <button
        type="button"
        className="nav-notification-bell-wrap nav-notification-bell-button"
        aria-label={showBadge ? `${activeCount} new notifications` : "Notifications"}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <span className={`nav-notification-bell-icon nav-notification-bell-icon--${accent}`}>
          <Bell className="nav-notification-bell-lucide" size={26} strokeWidth={1.75} aria-hidden />
        </span>
        {showBadge ? (
          <span className="nav-notification-bell-badge" aria-hidden>
            {activeCount > 99 ? "99+" : activeCount}
          </span>
        ) : null}
      </button>
      <NotificationDropdown
        open={open}
        role={role}
        accent={accent}
        loading={loading}
        notifications={notifications}
        authToken={readAuthToken(authTokenProp)}
        onSelectNotification={() => void refreshCount()}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
